import React, { useCallback, useMemo, useState } from 'react'

import { FiEdit2, FiPlus, FiTrash2 } from 'react-icons/fi'

import { Alert, Button, HeaderConfig, PageMenu, SimpleTable, TableSkeleton, Toast } from '@/components/ui'
import { useToast } from '@/hooks'
import { formatUserDate } from '@/utils/formatDate'

import DeleteStatusModal from './DeleteStatusModal'
import StatusFormModal from './StatusFormModal'
import useAlertStatuses from './useAlertStatuses'

import type { StatusFormMode, StatusRecord } from '../types'

const validateStatusName = (value: string): string => {
  const trimmed = value.trim()

  if (!trimmed) {
    return 'Status name is required.'
  }

  if (trimmed.length > 100) {
    return 'Status name must be 100 characters or less.'
  }

  return ''
}

const StatusManagement: React.FC = () => {
  const { statuses, isLoading, error, createStatus, updateStatus, deleteStatus } = useAlertStatuses()
  const [showFormModal, setShowFormModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [formMode, setFormMode] = useState<StatusFormMode>('add')
  const [activeStatus, setActiveStatus] = useState<StatusRecord | null>(null)
  const [statusNameInput, setStatusNameInput] = useState('')
  const [formError, setFormError] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const { toast, show, showToast, hideToast } = useToast()
  const isActionInProgress = isSaving || isDeleting || isLoading

  const openAddModal = useCallback(() => {
    if (isActionInProgress) return

    setFormMode('add')
    setActiveStatus(null)
    setStatusNameInput('')
    setFormError('')
    setShowFormModal(true)
  }, [isActionInProgress])

  const openEditModal = useCallback(
    (id: number) => {
      if (isActionInProgress) return

      const selected = statuses.find((status) => status.id === id)
      if (!selected) return

      setFormMode('edit')
      setActiveStatus(selected)
      setStatusNameInput(selected.statusName)
      setFormError('')
      setShowFormModal(true)
    },
    [isActionInProgress, statuses]
  )

  const openDeleteModal = useCallback(
    (id: number) => {
      if (isActionInProgress) return

      const selected = statuses.find((status) => status.id === id)
      if (!selected) return

      setActiveStatus(selected)
      setShowDeleteModal(true)
    },
    [isActionInProgress, statuses]
  )

  const closeFormModal = useCallback(() => {
    setShowFormModal(false)
    setFormError('')
  }, [])

  const closeDeleteModal = useCallback(() => {
    setShowDeleteModal(false)
    setActiveStatus(null)
  }, [])

  const handleSaveStatus = useCallback(async () => {
    const validationError = validateStatusName(statusNameInput)
    if (validationError) {
      setFormError(validationError)
      return
    }

    const trimmedName = statusNameInput.trim()
    setIsSaving(true)

    try {
      const result =
        formMode === 'add'
          ? await createStatus(trimmedName)
          : activeStatus
            ? await updateStatus(activeStatus.id, trimmedName)
            : 'Unable to edit status.'

      if (result) {
        setFormError(result)
        return
      }

      closeFormModal()
    } finally {
      setIsSaving(false)
    }
  }, [activeStatus, closeFormModal, createStatus, formMode, statusNameInput, updateStatus])

  const handleConfirmDelete = useCallback(async () => {
    if (!activeStatus) return

    setIsDeleting(true)

    try {
      const result = await deleteStatus(activeStatus.id)

      if (result) {
        closeDeleteModal()
        showToast({
          header: 'Delete Failed',
          body: result,
          position: 'end',
          warning: true,
        })
        return
      }

      closeDeleteModal()
    } finally {
      setIsDeleting(false)
    }
  }, [activeStatus, closeDeleteModal, deleteStatus, showToast])

  const body = useMemo(
    () =>
      statuses.map((status) => {
        return {
          'Status Name': status.statusName,
          'Created Date': {
            id: 'created-' + status.id,
            date: status.createdAt,
            display: formatUserDate(status.createdAt),
          },
          'Last Updated': {
            id: 'updated-' + status.id,
            date: status.updatedAt,
            display: formatUserDate(status.updatedAt),
          },
          Actions: {
            value: '',
            display: (
              <div className="flex items-center justify-center gap-2">
                <Button
                  type="button"
                  variation="ghost"
                  size="xs"
                  className="min-h-0! h-7! w-7! p-0!"
                  handleClick={() => openEditModal(status.id)}
                  ariaLabel={'Edit ' + status.statusName}
                  icon={<FiEdit2 className="h-4 w-4" />}
                />
                <Button
                  type="button"
                  variation="ghost"
                  size="xs"
                  className="min-h-0! h-7! w-7! p-0! text-error hover:bg-error/10"
                  handleClick={() => openDeleteModal(status.id)}
                  ariaLabel={'Delete ' + status.statusName}
                  icon={<FiTrash2 className="h-4 w-4" />}
                />
              </div>
            ),
          },
        }
      }),
    [openDeleteModal, openEditModal, statuses]
  )

  const headers: HeaderConfig[] = body[0]
    ? Object.keys(body[0])
    : ['Status Name', 'Created Date', 'Last Updated', 'Actions']

  return (
    <div className="flex flex-col pt-2 pb-2 pl-4 pr-4 bg-white rounded-md space-y-2">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center">
        <h2 className="text-lg text-primary">Action Statuses</h2>
        <PageMenu
          actions={[
            { id: 'add-status', label: 'Add Status', icon: <FiPlus />, onClick: openAddModal, variant: 'primary' },
          ]}
        />
      </div>
      <section className="h-full min-h-0">
        {error ? (
          <Alert type="error" message={error} />
        ) : isLoading && !statuses.length ? (
          <TableSkeleton columns={headers.length} withHeader rowHeight={35} />
        ) : body.length > 0 ? (
          <>
            <SimpleTable
              headers={headers}
              body={body}
              headerClassNames={{ Actions: 'w-28 text-center' }}
              cellClassNames={{ Actions: 'text-center' }}
            />
          </>
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-4">
            No action statuses have been created yet. Add a status to start tracking alert actions.
          </div>
        )}
      </section>

      <StatusFormModal
        show={showFormModal}
        mode={formMode}
        statusName={statusNameInput}
        originalStatusName={activeStatus ? activeStatus.statusName : ''}
        error={formError}
        isSaving={isSaving}
        onStatusNameChange={(value) => {
          setStatusNameInput(value)
          if (formError) {
            setFormError('')
          }
        }}
        onSave={handleSaveStatus}
        onCancel={closeFormModal}
      />

      <DeleteStatusModal
        show={showDeleteModal}
        statusName={activeStatus ? activeStatus.statusName : ''}
        isDeleting={isDeleting}
        onDelete={handleConfirmDelete}
        onCancel={closeDeleteModal}
      />

      <Toast
        show={show}
        onClose={hideToast}
        header={toast.header}
        body={toast.body}
        position={toast.position}
        success={toast.success}
        info={toast.info}
        warning={toast.warning}
        actions={toast.actions}
      />
    </div>
  )
}

export default StatusManagement
